// Bas-U: entreprenörsstyrning – bjud in entreprenörer till portalen och följ upp
// kvittenser och kompetensbevis.
import React, { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { HardHat, Plus, Copy, Ban, CheckCircle2, FileBadge } from "lucide-react";

interface Props {
  projectId: string;
}

interface Invite {
  id: string;
  company: string;
  contact_name: string | null;
  contact_email: string;
  trade: string | null;
  expires_at: string | null;
  revoked_at: string | null;
  open_count: number;
  last_opened_at: string | null;
  parent_invite_id: string | null;
  tier: number;
}

interface Ack {
  invite_id: string;
  document_type: string;
  acknowledged_by: string;
  acknowledged_at: string;
}

interface Comp {
  id: string;
  invite_id: string;
  person_name: string;
  competence_type: string;
  valid_until: string | null;
  file_name: string | null;
}

const randomToken = () => {
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  return Array.from(bytes)
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
};

const sha256Hex = async (input: string) => {
  const buf = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(input));
  return Array.from(new Uint8Array(buf))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
};

const ContractorManagement: React.FC<Props> = ({ projectId }) => {
  const [invites, setInvites] = useState<Invite[]>([]);
  const [acks, setAcks] = useState<Ack[]>([]);
  const [comps, setComps] = useState<Comp[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    company: "",
    contact_name: "",
    contact_email: "",
    trade: "",
    parent_invite_id: "",
    message: "",
    can_clear_work_preps: false,
  });
  const [newLink, setNewLink] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const [i, a, c] = await Promise.all([
      supabase
        .from("contractor_portal_invites")
        .select(
          "id, company, contact_name, contact_email, trade, expires_at, revoked_at, open_count, last_opened_at, parent_invite_id, tier"
        )
        .eq("project_id", projectId)
        .order("created_at", { ascending: true }),
      supabase
        .from("contractor_acknowledgements")
        .select("invite_id, document_type, acknowledged_by, acknowledged_at")
        .eq("project_id", projectId),
      supabase
        .from("contractor_competences")
        .select("id, invite_id, person_name, competence_type, valid_until, file_name")
        .eq("project_id", projectId),
    ]);
    if (i.error || a.error || c.error) toast.error("Kunde inte hämta entreprenörer");
    setInvites((i.data || []) as Invite[]);
    setAcks((a.data || []) as Ack[]);
    setComps((c.data || []) as Comp[]);
    setLoading(false);
  }, [projectId]);

  useEffect(() => {
    load();
  }, [load]);

  const createInvite = async () => {
    if (!form.company.trim() || !form.contact_email.trim()) {
      toast.error("Företag och e-post krävs");
      return;
    }
    const token = randomToken();
    const token_hash = await sha256Hex(token);
    const { error } = await supabase.from("contractor_portal_invites").insert({
      project_id: projectId,
      company: form.company.trim(),
      contact_name: form.contact_name.trim() || null,
      contact_email: form.contact_email.trim(),
      trade: form.trade.trim() || null,
      parent_invite_id: form.parent_invite_id || null,
      tier: form.parent_invite_id
        ? (invites.find((i) => i.id === form.parent_invite_id)?.tier ?? 1) + 1
        : 1,
      message: form.message.trim() || null,
      can_clear_work_preps: form.can_clear_work_preps,
      token_hash,
      expires_at: new Date(Date.now() + 1000 * 60 * 60 * 24 * 180).toISOString(),
    });
    if (error) {
      toast.error("Kunde inte skapa portalåtkomst");
      return;
    }
    const link = `${window.location.origin}/entreprenor/${token}`;
    setNewLink(link);
    await navigator.clipboard.writeText(link).catch(() => undefined);
    toast.success("Länk skapad och kopierad");
    setForm({
      company: "",
      contact_name: "",
      contact_email: "",
      trade: "",
      parent_invite_id: "",
      message: "",
      can_clear_work_preps: false,
    });
    load();
  };

  const revoke = async (id: string) => {
    const { error } = await supabase
      .from("contractor_portal_invites")
      .update({ revoked_at: new Date().toISOString() })
      .eq("id", id);
    if (error) toast.error("Kunde inte återkalla");
    else {
      toast.success("Åtkomsten återkallad");
      load();
    }
  };

  const ackFor = (inviteId: string, type: string) =>
    acks.find((a) => a.invite_id === inviteId && a.document_type === type);

  /** Alla led under en huvudentreprenör, inklusive den själv. */
  const branchOf = (rootId: string): Invite[] => {
    const out: Invite[] = [];
    const walk = (id: string) => {
      const node = invites.find((i) => i.id === id);
      if (!node) return;
      out.push(node);
      invites.filter((i) => i.parent_invite_id === id).forEach((child) => walk(child.id));
    };
    walk(rootId);
    return out;
  };

  const roots = invites.filter((i) => !i.parent_invite_id);
  const REQUIRED_ACKS = ["amp", "ordningsregler"] as const;

  const rollups = roots.map((root) => {
    const branch = branchOf(root.id).filter((i) => !i.revoked_at);
    const required = branch.length * REQUIRED_ACKS.length;
    const done = branch.reduce(
      (sum, i) => sum + REQUIRED_ACKS.filter((t) => ackFor(i.id, t)).length,
      0
    );
    const missing = branch.filter((i) => REQUIRED_ACKS.some((t) => !ackFor(i.id, t)));
    const competences = comps.filter((c) => branch.some((i) => i.id === c.invite_id));
    const expired = competences.filter(
      (c) => c.valid_until && new Date(c.valid_until) < new Date()
    );
    return {
      root,
      count: branch.length,
      maxTier: Math.max(...branch.map((i) => i.tier || 1)),
      required,
      done,
      percent: required ? Math.round((done / required) * 100) : 100,
      missing,
      competences: competences.length,
      expired: expired.length,
    };
  });



  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <div>
            <CardTitle className="flex items-center gap-2">
              <HardHat className="h-5 w-5" /> Entreprenörer
            </CardTitle>
            <p className="mt-1 text-sm text-muted-foreground">
              Bjud in entreprenörer till en portal utan systemkonto. De kvitterar AMP och
              ordningsregler och lämnar in kompetensbevis.
            </p>
          </div>
          <Button onClick={() => setOpen(true)}>
            <Plus className="mr-1 h-4 w-4" /> Ny entreprenör
          </Button>
        </CardHeader>
        {newLink && (
          <CardContent>
            <div className="flex items-center gap-2 rounded-md border bg-muted/40 p-2">
              <code className="flex-1 truncate text-xs">{newLink}</code>
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  navigator.clipboard.writeText(newLink);
                  toast.success("Kopierad");
                }}
              >
                <Copy className="h-4 w-4" />
              </Button>
            </div>
          </CardContent>
        )}
      </Card>

      {!loading && rollups.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Kvittenser per entreprenörsgren</CardTitle>
            <p className="text-sm text-muted-foreground">
              Summering av hela kedjan under varje huvudentreprenör – alla underentreprenörer i
              samtliga led räknas med.
            </p>
          </CardHeader>
          <CardContent className="space-y-3">
            {rollups.map((r) => (
              <div key={r.root.id} className="rounded-md border p-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <p className="text-sm font-medium">{r.root.company}</p>
                    <p className="text-xs text-muted-foreground">
                      {r.count} företag i grenen · {r.maxTier} led · {r.competences} kompetensbevis
                      {r.expired > 0 ? ` (${r.expired} utgångna)` : ""}
                    </p>
                  </div>
                  <Badge variant={r.percent === 100 ? "default" : "outline"}>
                    {r.done}/{r.required} kvitterat · {r.percent}%
                  </Badge>
                </div>
                <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-muted">
                  <div
                    className={`h-full ${r.percent === 100 ? "bg-emerald-500" : "bg-amber-500"}`}
                    style={{ width: `${r.percent}%` }}
                  />
                </div>
                {r.missing.length > 0 && (
                  <p className="mt-2 text-xs text-muted-foreground">
                    Saknar kvittens: {r.missing.map((m) => m.company).join(", ")}
                  </p>
                )}
              </div>
            ))}
          </CardContent>
        </Card>
      )}


      {loading && <p className="text-sm text-muted-foreground">Laddar…</p>}
      {!loading && invites.length === 0 && (
        <p className="text-sm text-muted-foreground">Inga entreprenörer inbjudna ännu.</p>
      )}

      {[...invites]
        .sort((a, b) => {
          const rootA = a.parent_invite_id ?? a.id;
          const rootB = b.parent_invite_id ?? b.id;
          if (rootA !== rootB) return rootA.localeCompare(rootB);
          return (a.tier || 1) - (b.tier || 1);
        })
        .map((inv) => {
        const invComps = comps.filter((c) => c.invite_id === inv.id);
        const parent = invites.find((i) => i.id === inv.parent_invite_id);
        return (
          <Card key={inv.id} style={{ marginLeft: Math.min((inv.tier || 1) - 1, 3) * 16 }}>
            <CardHeader className="flex flex-row items-start justify-between space-y-0">
              <div>
                <CardTitle className="text-base">
                  {inv.company}
                  {inv.trade ? ` – ${inv.trade}` : ""}
                </CardTitle>
                {parent && (
                  <p className="text-xs text-muted-foreground">
                    Underentreprenör i led {inv.tier} till {parent.company}
                  </p>
                )}
                <p className="text-sm text-muted-foreground">
                  {inv.contact_name ? `${inv.contact_name} · ` : ""}
                  {inv.contact_email} · öppnad {inv.open_count} ggr
                </p>
              </div>
              <div className="flex items-center gap-2">
                {inv.revoked_at ? (
                  <Badge variant="destructive">Återkallad</Badge>
                ) : (
                  <Button variant="ghost" size="sm" onClick={() => revoke(inv.id)}>
                    <Ban className="mr-1 h-4 w-4" /> Återkalla
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-2">
              <div className="flex flex-wrap gap-2">
                {(["amp", "ordningsregler"] as const).map((t) => {
                  const a = ackFor(inv.id, t);
                  return (
                    <Badge key={t} variant={a ? "default" : "outline"} className="gap-1">
                      {a && <CheckCircle2 className="h-3 w-3" />}
                      {t === "amp" ? "AMP" : "Ordningsregler"}
                      {a ? ` – ${a.acknowledged_by}` : " – ej kvitterad"}
                    </Badge>
                  );
                })}
              </div>
              <div className="space-y-1">
                <p className="flex items-center gap-1 text-sm font-medium">
                  <FileBadge className="h-4 w-4" /> Kompetensbevis ({invComps.length})
                </p>
                {invComps.length === 0 && (
                  <p className="text-sm text-muted-foreground">Inga bevis inlämnade.</p>
                )}
                {invComps.map((c) => (
                  <div key={c.id} className="rounded-md border p-2 text-sm">
                    {c.person_name} · {c.competence_type}
                    {c.valid_until && (
                      <span className="text-muted-foreground"> · t.o.m. {c.valid_until}</span>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        );
      })}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Ny entreprenör</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Företag *</Label>
              <Input
                value={form.company}
                onChange={(e) => setForm({ ...form, company: e.target.value })}
              />
            </div>
            <div>
              <Label>Kontaktperson</Label>
              <Input
                value={form.contact_name}
                onChange={(e) => setForm({ ...form, contact_name: e.target.value })}
              />
            </div>
            <div>
              <Label>E-post *</Label>
              <Input
                type="email"
                value={form.contact_email}
                onChange={(e) => setForm({ ...form, contact_email: e.target.value })}
              />
            </div>
            <div>
              <Label>Yrkesgrupp</Label>
              <Input
                placeholder="t.ex. El, Stomme, Mark"
                value={form.trade}
                onChange={(e) => setForm({ ...form, trade: e.target.value })}
              />
            </div>
            <div>
              <Label>Underentreprenör till</Label>
              <select
                className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                value={form.parent_invite_id}
                onChange={(e) => setForm({ ...form, parent_invite_id: e.target.value })}
              >
                <option value="">Ingen – kontrakterad direkt</option>
                {invites
                  .filter((i) => !i.revoked_at)
                  .map((i) => (
                    <option key={i.id} value={i.id}>
                      {i.company}
                    </option>
                  ))}
              </select>
            </div>
            <div>
              <Label>Meddelande</Label>
              <Textarea
                rows={3}
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
              />
            </div>
            <label className="flex items-start gap-2 rounded-md border p-3 text-sm">
              <input
                type="checkbox"
                className="mt-0.5 h-4 w-4 accent-current"
                checked={form.can_clear_work_preps}
                onChange={(e) => setForm({ ...form, can_clear_work_preps: e.target.checked })}
              />
              <span>
                Får kvittera startbesked för arbetsberedningar
                <span className="block text-xs text-muted-foreground">
                  Entreprenören kan då kvittera godkända arbetsberedningar direkt i sin länk.
                  Kvittensen syns i systemet med namn och tidpunkt.
                </span>
              </span>
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>
              Avbryt
            </Button>
            <Button
              onClick={async () => {
                await createInvite();
                setOpen(false);
              }}
            >
              Skapa länk
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ContractorManagement;
